import { useLazyQuery } from '@apollo/client';
import { useTheme } from '@mui/material';
import {
  getFriendlyConversationId,
  parseFriendlyConversationId,
} from '@provision/formatting-utils';
import { portalPaths } from '@provision/portal-routes/dist/paths';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { groupBy, isNullish } from 'remeda';
import {
  DATA_CONVERSATION_FRIENDLY_ID,
  DATA_ENTITY_TYPE,
  DATA_PARTNER_PREFIX,
} from 'src/components/TextWithUI/const';
import { UIReplacer, UIReplacerName } from 'src/components/TextWithUI/types';
import {
  Conversation,
  GET_CONVERSATION_QUERY,
  GetConversationQuery,
  usePartners,
} from 'src/graphql';
import { ConditionInput, QueryVariables } from 'src/graphql/types';
import { getPartnerFriendlyIdentifier } from 'src/routes/hooks/use-sync-partner-url-access';
import { useGlobalStore } from 'src/store';
import { useNavigateRoute } from 'src/utils/routing/use-navigate-route';
import { ChatConversationLink } from './use-text-with-ui-conversation.element';

type MetaDataType = Record<string, Conversation>;

const LEGACY_HOSTS: string[] = []; // ... if any
const CURRENT_HOST = window.location.host;

const LINK_PATTERN =
  /<a[^>]*?href=["']((?:https?:\/\/)?([^/"']+)?(?:\/([^/"']+))?(?:\/[^/"']*)*?\/conversations\/([^/"']+))["'][^>]*?>(.*?)<\/a>/gim;
const isInternalLink = (host: string) => {
  return !host || host === CURRENT_HOST || LEGACY_HOSTS.includes(host);
};

const getConversationFilters = (conversationFriendlyIds: string[]) => {
  const parsedIds = conversationFriendlyIds.map(parseFriendlyConversationId).filter(Boolean);

  const groupedByPartner = groupBy(parsedIds, (it) => it.partnerId || '');

  const filters: ConditionInput[][] = [];

  Object.keys(groupedByPartner).forEach((partnerId) => {
    if (!isNullish(partnerId)) {
      const parsedItems = groupedByPartner[partnerId];
      const ids = parsedItems.map((it) => it.workspaceConversationId).filter(Boolean);
      if (ids.length) {
        filters.push([
          {
            attribute: 'workspaceConversationId',
            operator: 'in',
            value: ids.join(','),
          },
          {
            attribute: 'partnerId',
            value: partnerId,
            operator: '=',
          },
        ]);
      }
    }
  });

  return filters;
};

const useReplacementElement = () => {
  const theme = useTheme();
  const themeVariables = useMemo(
    () => ({
      chipIconColor: theme.palette.custom.OV.sys.tertiary,
      chipBorderRadius: theme.spacing(0.5),
      chipBackgroundColor: theme.palette.custom.OV.sys.surface,
    }),
    [theme],
  );

  const getReplacementElement = useCallback(
    ({
      conversationFriendlyId,
      conversationSubject,
      partnerPrefix,
    }: {
      conversationFriendlyId: string;
      conversationSubject: string;
      partnerPrefix: string;
    }) =>
      renderToStaticMarkup(
        <ChatConversationLink
          conversationFriendlyId={conversationFriendlyId}
          themeVariables={themeVariables}
          conversationSubject={conversationSubject}
          partnerPrefix={partnerPrefix}
        />,
      ),
    [themeVariables],
  );

  const replacementElementParentStyles = useMemo(
    () => ({
      [`& [${DATA_ENTITY_TYPE}="${UIReplacerName.ConversationLink}"]:hover`]: {
        backgroundColor: `${theme.palette.custom.OV.sys.surfaceContainer} !important`,
      },
    }),
    [theme.palette.custom.OV.sys.surfaceContainer],
  );

  return useMemo(
    () => ({ getReplacementElement, replacementElementParentStyles }),
    [getReplacementElement, replacementElementParentStyles],
  );
};

export const useTextWithUIConversation = (): UIReplacer => {
  const { navigate } = useNavigateRoute();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { partners } = usePartners();

  const [getConversations] = useLazyQuery<GetConversationQuery, QueryVariables>(
    GET_CONVERSATION_QUERY,
  );
  const [metaData, setMetaData] = useState<MetaDataType>({} as MetaDataType);
  const metaDataRef = useRef(metaData);

  const { getReplacementElement, replacementElementParentStyles } = useReplacementElement();

  useEffect(() => {
    metaDataRef.current = metaData;
  }, [metaData]);

  const loadMetaData = useCallback(
    async (input: string) => {
      const metaDataSnapshot = metaDataRef.current;
      const conversationFriendlyIdsMap = new Map<string, Conversation | null>();
      input.matchAll(LINK_PATTERN).forEach((match) => {
        const [, , host, , friendlyId] = match;

        if (isInternalLink(host)) {
          conversationFriendlyIdsMap.set(friendlyId, null);
        }
      });

      const newIdsToFetch = Array.from(conversationFriendlyIdsMap.keys()).filter(
        (id) => id in metaDataSnapshot === false,
      );

      if (newIdsToFetch.length === 0) {
        return Promise.resolve(metaDataSnapshot);
      } else {
        const allFilters = getConversationFilters(newIdsToFetch);
        if (allFilters.length === 0) {
          return Promise.resolve(metaDataSnapshot);
        }

        const results = await Promise.all(
          allFilters.map((filters) =>
            getConversations({
              variables: {
                params: {
                  filters,
                  includeDeleted: true,
                },
              },
            }),
          ),
        );

        const newMetadata = results
          .flatMap((result) => result.data?.getConversation?.records || [])
          .reduce(
            (acc, it) => ({
              ...acc,
              [getFriendlyConversationId(it, it.partnerId)]: it,
            }),
            {},
          );

        if (Object.keys(newMetadata).length === 0) {
          return Promise.resolve(metaDataSnapshot);
        }

        const mergedMetaData = { ...metaDataSnapshot, ...newMetadata };
        metaDataRef.current = mergedMetaData;
        setMetaData((prev) => ({ ...prev, ...newMetadata }));

        return Promise.resolve(mergedMetaData);
      }
    },
    [getConversations],
  );

  const replace = useCallback(
    (input: string, metaData?: MetaDataType) => {
      return input.replace(
        LINK_PATTERN,
        (
          fullMatch: string,
          _: string,
          host: string,
          urlPartnerPrefix: string,
          conversationFriendlyId: string,
          // eslint-disable-next-line max-params
        ) => {
          let partnerPrefix = urlPartnerPrefix;
          if (!conversationFriendlyId) {
            return fullMatch;
          }
          const { partnerId } = parseFriendlyConversationId(conversationFriendlyId);

          const conversationMeta = metaData?.[conversationFriendlyId];
          const conversationPartnerId = partnerId || conversationMeta?.partnerId;

          if (conversationPartnerId) {
            const conversationPartner = partners?.[conversationPartnerId];

            if (conversationPartner) {
              // partnerPrefix can be present in URL but it is more reliable
              // to get it from partner record if possible, if not - fallback to URL value
              partnerPrefix =
                getPartnerFriendlyIdentifier(conversationPartner) ?? urlPartnerPrefix;
            }
          }
          return isInternalLink(host)
            ? getReplacementElement({
                conversationFriendlyId,
                conversationSubject: conversationMeta?.subject || '',
                partnerPrefix,
              })
            : fullMatch;
        },
      );
    },
    [getReplacementElement, partners],
  );

  const onClick = useCallback(
    (element: HTMLElement) => {
      const conversationFriendlyId = element.getAttribute(DATA_CONVERSATION_FRIENDLY_ID) || '';
      const partnerPrefix = element.getAttribute(DATA_PARTNER_PREFIX) || '';

      if (!conversationFriendlyId) {
        return;
      }

      const { partnerId } = parseFriendlyConversationId(conversationFriendlyId);
      const isCrossPartnerInIsolation =
        !isNullish(selectedPartnerId) &&
        !!partnerId &&
        partnerId !== selectedPartnerId.toString();

      if (isCrossPartnerInIsolation) {
        window.open(
          `${partnerPrefix ? `/${partnerPrefix}` : ''}${portalPaths.conversation.full(conversationFriendlyId)}`,
          '_blank',
        );
      } else {
        navigate(portalPaths.conversation.full(conversationFriendlyId));
      }
    },
    [navigate, selectedPartnerId],
  );

  return useMemo(
    () => ({
      name: UIReplacerName.ConversationLink,
      replace,
      parentStyles: replacementElementParentStyles,
      loadMetaData,
      handlers: {
        onClick,
      },
    }),
    [replace, onClick, replacementElementParentStyles, loadMetaData],
  );
};
